/**
 * TruckTimeline Component
 * Main container for truck service timeline visualization
 */

import React, { useState, useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import TimelineChart from './TimelineChart';
import TimelineSlider from './TimelineSlider';
import { generateTimelineData, getTodayDate } from './utils';
import type { Truck, TimelineViewport } from './types';

interface TruckTimelineProps {
  trucks: Truck[];
  defaultDays?: number;
  height?: number;
}

export const TruckTimeline: React.FC<TruckTimelineProps> = ({
  trucks,
  defaultDays = 90,
  height,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize date range state (today to today + defaultDays)
  const today = useMemo(() => getTodayDate(), []);
  const initialEndDate = useMemo(() => {
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + defaultDays);
    return endDate;
  }, [today, defaultDays]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(initialEndDate);

  // Calculate min/max boundaries (ALWAYS start today)
  // Mobile: 6 months max (180 days), Desktop: 1 year max (365 days)
  const minDate = useMemo(() => today, [today]);
  const maxDate = useMemo(() => {
    const maxDays = isMobile ? 180 : 365; // 6 months mobile, 1 year desktop
    const maxDateCalc = new Date(today);
    maxDateCalc.setDate(today.getDate() + maxDays);
    return maxDateCalc;
  }, [today, isMobile]);

  // Calculate viewport from date range
  const viewport = useMemo((): TimelineViewport => {
    const monthsRange = Math.round(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    return {
      startDate,
      endDate,
      monthsRange,
    };
  }, [startDate, endDate]);

  // Generate timeline data from trucks
  const timelineData = useMemo(() => {
    return generateTimelineData(trucks);
  }, [trucks]);

  // Handle range change from slider (ensure start date is always today)
  const handleRangeChange = (newStartDate: Date, newEndDate: Date) => {
    // Clamp start date to today (cannot view dates before today)
    const clampedStartDate = newStartDate < today ? today : newStartDate;

    // Clamp end date to max range (365 days from today)
    const clampedEndDate = newEndDate > maxDate ? maxDate : newEndDate;

    setStartDate(clampedStartDate);
    setEndDate(clampedEndDate);
  };

  // Handle zoom in on clicked date (reduce range by half, minimum 8 days)
  const handleZoomIn = (clickedDate: Date) => {
    const currentRangeMs = endDate.getTime() - startDate.getTime();
    const minRangeMs = 8 * 24 * 60 * 60 * 1000; // 8 days minimum

    // Check if we're already at minimum zoom
    if (currentRangeMs <= minRangeMs) {
      return; // Cannot zoom in further
    }

    const newRangeMs = Math.max(minRangeMs, currentRangeMs / 2);

    // Calculate new start and end dates centered on clicked date
    let newStartDate = new Date(clickedDate.getTime() - newRangeMs / 2);
    let newEndDate = new Date(clickedDate.getTime() + newRangeMs / 2);

    // Clamp to valid boundaries
    if (newStartDate < today) {
      newStartDate = today;
      newEndDate = new Date(today.getTime() + newRangeMs);
    }

    if (newEndDate > maxDate) {
      newEndDate = maxDate;
      newStartDate = new Date(maxDate.getTime() - newRangeMs);
    }

    // Ensure we don't go below today after clamping
    if (newStartDate < today) {
      newStartDate = today;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Handle zoom out on clicked date (double range)
  // Mobile: maximum 180 days (6 months), Desktop: maximum 365 days (1 year)
  const handleZoomOut = (clickedDate: Date) => {
    const currentRangeMs = endDate.getTime() - startDate.getTime();
    const maxDays = isMobile ? 180 : 365;
    const maxRangeMs = maxDays * 24 * 60 * 60 * 1000;

    // Check if we're already at maximum zoom
    if (currentRangeMs >= maxRangeMs) {
      return; // Cannot zoom out further
    }

    const newRangeMs = Math.min(maxRangeMs, currentRangeMs * 2);

    // Calculate new start and end dates centered on clicked date
    let newStartDate = new Date(clickedDate.getTime() - newRangeMs / 2);
    let newEndDate = new Date(clickedDate.getTime() + newRangeMs / 2);

    // Clamp to valid boundaries
    if (newStartDate < today) {
      newStartDate = today;
      newEndDate = new Date(today.getTime() + newRangeMs);
    }

    if (newEndDate > maxDate) {
      newEndDate = maxDate;
      newStartDate = new Date(maxDate.getTime() - newRangeMs);
    }

    // Ensure we don't go below today after clamping
    if (newStartDate < today) {
      newStartDate = today;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Empty state
  if (trucks.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: '#6b7280',
          fontSize: '0.875rem',
          backgroundColor: '#f9fafb',
          borderRadius: 1,
          border: '1px solid #e5e7eb',
        }}
      >
        No vehicles to display
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      {/* Timeline Chart */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 1,
          border: '1px solid #e5e7eb',
          p: { xs: 1, sm: 1.5, md: 2 },
          minHeight: { xs: 300, sm: 350, md: 400 },
          maxHeight: { xs: 500, md: 600 },
          overflowY: 'auto',
          overflowX: 'auto',
          // Smooth scrolling on mobile
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <TimelineChart
          data={timelineData}
          viewport={viewport}
          height={height}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          isMobile={isMobile}
          isSmallMobile={isSmallMobile}
        />
      </Box>

      {/* Timeline Range Slider (Premiere Pro Style) */}
      <TimelineSlider
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        maxDate={maxDate}
        onRangeChange={handleRangeChange}
        isMobile={isMobile}
        isSmallMobile={isSmallMobile}
      />
    </Box>
  );
};

export default TruckTimeline;
